import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import type {
  AdminEssayDraft,
  AdminOrganization,
  AdminUser,
  AuthResponse,
  EssayExample,
  EssaySectionForDisplay,
  EssayTemplate,
  FaqItem,
  GenerateEssayResponse,
  PaymentCapturePayload,
  PayPalPlanIds,
  EssayLimits,
  ProfileUser,
  SchoolSearchGroup,
  SignupResponse,
  Subscription,
  SubscriptionPayload,
  Testimonial,
} from './api.types';


/** In dev, use empty base so ng serve proxy (proxy.conf.json) forwards /api to backend. */
const API_BASE = isDevMode() ? 'http://localhost:9100' : 'https://cogitationes.5searches.com';


/** Re-export types for consumers that import from api.service. */
export type {
  AdminEssayDraft,
  AdminOrganization,
  AdminUser,
  AuthResponse,
  EssayExample,
  EssaySectionForDisplay,
  EssayTemplate,
  FaqItem,
  GenerateEssayResponse,
  PaymentCapturePayload,
  ProfileUser,
  PayPalPlanIds,
  EssayLimits,
  SignupResponse,
  Subscription,
  SubscriptionPayload,
  Testimonial,
} from './api.types';


@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private http: HttpClient) {}

  login(email: string, password: string, browserInfo?: string): Observable<AuthResponse | { error: string }> {
    const body: Record<string, string> = { email, password };
    if (browserInfo) body['browserInfo'] = browserInfo;
    return this.http.post<AuthResponse | { error: string }>(
      `${API_BASE}/api/auth/login`,
      body
    );
  }

  /** Record logout event (browser info). Call before clearing auth. Requires valid token. */
  logout(browserInfo?: string): Observable<{ message: string } | { error: string }> {
    const body = browserInfo ? { browserInfo } : {};
    return this.http.post<{ message: string } | { error: string }>(
      `${API_BASE}/api/auth/logout`,
      body
    );
  }

  signup(email: string, password: string, firstName?: string, lastName?: string, organizationCode?: string, referralCode?: string): Observable<SignupResponse | { error: string }> {
    const body: Record<string, unknown> = { email, password, firstName, lastName };
    if (organizationCode) body['organizationCode'] = organizationCode;
    if (referralCode) body['referralCode'] = referralCode;
    return this.http.post<SignupResponse | { error: string }>(
      `${API_BASE}/api/auth/signup`,
      body
    );
  }

  verifyEmail(token: string): Observable<{ message: string } | { error: string }> {
    return this.http.get<{ message: string } | { error: string }>(
      `${API_BASE}/api/auth/verify-email`,
      { params: { token } }
    );
  }

  /** Request a password reset email. Sends a link to the email if the account exists. */
  requestResetPassword(email: string): Observable<{ message: string } | { error: string }> {
    return this.http.post<{ message: string } | { error: string }>(
      `${API_BASE}/api/auth/request-reset-password`,
      { email: email.trim() }
    );
  }

  /** Submit public contact form. Notifies admins by email when SMTP is configured. */
  submitContact(body: { name: string; email: string; subject?: string; message: string }): Observable<{ message: string } | { error: string }> {
    return this.http.post<{ message: string } | { error: string }>(
      `${API_BASE}/api/contact`,
      body
    );
  }

  /** Set new password using reset token (from email link). */
  resetPassword(token: string, newPassword: string): Observable<{ message: string } | { error: string }> {
    return this.http.post<{ message: string } | { error: string }>(
      `${API_BASE}/api/auth/reset-password`,
      { token, newPassword }
    );
  }

  /** Get current user profile. Requires auth. */
  getMe(): Observable<{ user: ProfileUser } | { error: string }> {
    return this.http.get<{ user: ProfileUser } | { error: string }>(`${API_BASE}/api/auth/me`);
  }

  /** Update current user profile (firstName, lastName, phone, school, major). Requires auth. */
  updateProfile(body: { firstName?: string; lastName?: string; phone?: string; school?: string; major?: string }): Observable<{ user: ProfileUser } | { error: string }> {
    return this.http.patch<{ user: ProfileUser } | { error: string }>(`${API_BASE}/api/auth/me`, body);
  }

  /** Search universities by name for school autocomplete. Returns { universities: { name, country? }[] }. */
  searchUniversities(query: string): Observable<{ universities: { name: string; country?: string }[] }> {
    return this.http.get<{ universities: { name: string; country?: string }[] }>(
      `${API_BASE}/api/universities`,
      { params: { q: query } }
    ).pipe(catchError(() => of({ universities: [] })));
  }

  /** Search schools (universities + high schools) grouped. Returns { groups: { type, label, items }[] }. */
  searchSchools(query: string): Observable<{ groups: SchoolSearchGroup[] }> {
    return this.http.get<{ groups: SchoolSearchGroup[] }>(
      `${API_BASE}/api/schools`,
      { params: { q: query } }
    ).pipe(catchError(() => of({ groups: [] })));
  }

  /** Admin login. Returns token for admin endpoints. Separate from user login. */
  adminLogin(email: string, password: string): Observable<{ token: string; email: string } | { error: string }> {
    return this.http.post<{ token: string; email: string } | { error: string }>(
      `${API_BASE}/api/admin/login`,
      { email, password }
    );
  }

  /** Admin: list all users with subscription. Requires admin auth. */
  getAdminUsers(): Observable<{ users: AdminUser[] } | { error: string }> {
    return this.http.get<{ users: AdminUser[] } | { error: string }>(`${API_BASE}/api/admin/users`);
  }

  /** Admin: send password reset email to user. Requires admin auth. */
  adminTriggerResetPassword(userId: string): Observable<{ message: string } | { error: string }> {
    return this.http.post<{ message: string } | { error: string }>(
      `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/trigger-reset-password`,
      {}
    );
  }

  /** Admin: lock or unlock user account. Requires admin auth. */
  adminSetUserLock(userId: string, locked: boolean): Observable<{ message: string; locked: boolean } | { error: string }> {
    return this.http.post<{ message: string; locked: boolean } | { error: string }>(
      `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/lock`,
      { locked }
    );
  }

  /** Admin: enable or disable admin access on a user account. Requires admin auth. */
  adminSetUserAdmin(userId: string, isAdmin: boolean): Observable<{ message: string; isAdmin: boolean } | { error: string }> {
    return this.http.post<{ message: string; isAdmin: boolean } | { error: string }>(
      `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/admin`,
      { isAdmin }
    );
  }

  /** Admin: set user subscription. Requires admin auth. */
  adminUpdateSubscription(
    userId: string,
    body: { plan_id: string; plan_name?: string; price?: string; period?: string }
  ): Observable<{ message: string; plan_id: string; plan_name: string } | { error: string }> {
    return this.http.post<{ message: string; plan_id: string; plan_name: string } | { error: string }>(
      `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/subscription`,
      body
    );
  }

  /** Admin: list a user's essay drafts (readonly). Requires admin auth. */
  getAdminUserEssays(userId: string): Observable<{ essays: AdminEssayDraft[] } | { error: string }> {
    return this.http.get<{ essays: AdminEssayDraft[] } | { error: string }>(
      `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/essays`
    );
  }

  /**
   * Admin: update a user's essay draft. Requires admin auth.
   * - `generateEssayCount`: set the regeneration counter directly.
   * - `undoFinalize`: when true, clears the persisted generated essay so the
   *   draft becomes editable again on the user's next reload (also resets
   *   `generateEssayCount` to 0).
   */
  adminUpdateUserEssay(
    userId: string,
    draftId: string,
    body: { generateEssayCount?: number; undoFinalize?: boolean }
  ): Observable<{ message: string; generateEssayCount?: number; undoFinalize?: boolean } | { error: string }> {
    return this.http.patch<{ message: string; generateEssayCount?: number; undoFinalize?: boolean } | { error: string }>(
      `${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/essays/${encodeURIComponent(draftId)}`,
      body
    );
  }

  /** Admin: list organizations with user count and max_users. Requires admin auth. */
  getAdminOrganizations(): Observable<{ organizations: AdminOrganization[] } | { error: string }> {
    return this.http.get<{ organizations: AdminOrganization[] } | { error: string }>(
      `${API_BASE}/api/admin/organizations`
    );
  }

  /** Admin: create organization. Requires admin auth. */
  adminCreateOrganization(body: {
    code: string;
    name?: string;
    active?: boolean;
    max_users?: number | null;
    domain?: string;
    theme?: string;
    logo_data_url?: string | null;
  }): Observable<{ message: string; code: string } | { error: string }> {
    return this.http.post<{ message: string; code: string } | { error: string }>(
      `${API_BASE}/api/admin/organizations`,
      body
    );
  }

  /** Admin: update organization (name, active, max_users, domain). Requires admin auth. */
  adminUpdateOrganization(
    code: string,
    body: {
      name?: string;
      active?: boolean;
      max_users?: number | null;
      domain?: string;
      theme?: string | null;
      logo_data_url?: string | null;
    }
  ): Observable<{ message: string; code: string } | { error: string }> {
    return this.http.patch<{ message: string; code: string } | { error: string }>(
      `${API_BASE}/api/admin/organizations/${encodeURIComponent(code)}`,
      body
    );
  }

  getTemplates(): Observable<EssayTemplate[]> {
    return this.http.get<EssayTemplate[]>(`${API_BASE}/api/templates`).pipe(
      catchError(() => of([]))
    );
  }

  getEssaySections(): Observable<EssaySectionForDisplay[]> {
    return this.http.get<EssaySectionForDisplay[]>(`${API_BASE}/api/essay-sections`).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * Generate essay-builder prompts tailored to the paper title (Gemini). Requires auth.
   */
  generateEssayPrompts(body: {
    title: string;
    pageCount: number;
    template?: string;
    draftId: string;
  }): Observable<
    { sections: EssaySectionForDisplay[]; tailoredPromptsCount: number } | { error: string }
  > {
    return this.http
      .post<{ sections: EssaySectionForDisplay[]; tailoredPromptsCount: number }>(
        `${API_BASE}/api/essay-prompts/generate`,
        body
      )
      .pipe(
        catchError((err: { error?: { error?: string } }) => {
          const msg = err?.error && typeof err.error === 'object' && err.error.error;
          return of(typeof msg === 'string' ? { error: msg } : { error: 'Failed to generate prompts' });
        })
      );
  }

  getTestimonials(): Observable<Testimonial[]> {
    return this.http.get<Testimonial[]>(`${API_BASE}/api/testimonials`).pipe(
      catchError(() => of([]))
    );
  }

  getFaq(): Observable<FaqItem[]> {
    return this.http.get<FaqItem[]>(`${API_BASE}/api/faq`).pipe(
      catchError(() => of([]))
    );
  }

  getEssayExamples(): Observable<EssayExample[]> {
    return this.http.get<EssayExample[]>(`${API_BASE}/api/essay-examples`).pipe(
      catchError(() => of([]))
    );
  }

  createSubscription(payload: SubscriptionPayload): Observable<{ id: number; status: string } | { error: string }> {
    return this.http.post<{ id: number; status: string } | { error: string }>(
      `${API_BASE}/api/subscriptions`,
      payload
    );
  }

  capturePayment(payload: PaymentCapturePayload): Observable<{ subscription_id: number; status: string } | { error: string }> {
    return this.http.post<{ subscription_id: number; status: string } | { error: string }>(
      `${API_BASE}/api/payments/capture`,
      payload
    );
  }

  getPayPalPlanIds(): Observable<PayPalPlanIds> {
    return this.http.get<PayPalPlanIds>(`${API_BASE}/api/paypal/plan-ids`).pipe(
      catchError(() => of({}))
    );
  }

  getMySubscriptions(): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(`${API_BASE}/api/subscriptions`).pipe(
      catchError(() => of([]))
    );
  }

  /** Max pages per essay and max drafts for the current user. Requires auth. */
  getEssayLimits(): Observable<EssayLimits> {
    return this.http.get<EssayLimits>(`${API_BASE}/api/essay-limits`).pipe(
      catchError(() => of({ planId: null, maxPages: 2, maxDrafts: 1 }))
    );
  }

  /** Get writing autocomplete suggestions (Gmail-style). Requires auth. */
  getWritingSuggestions(text: string, promptContext?: string): Observable<{ suggestions: string[] }> {
    return this.http
      .post<{ suggestions: string[] }>(`${API_BASE}/api/writing/suggest`, { text, promptContext })
      .pipe(catchError(() => of({ suggestions: [] })));
  }

  cancelSubscriptionToFree(): Observable<{ message: string; cancelled_count: number } | { error: string }> {
    return this.http.post<{ message: string; cancelled_count: number } | { error: string }>(
      `${API_BASE}/api/subscriptions/cancel-to-free`,
      {}
    );
  }

  getEssayDrafts(): Observable<{ _id: string; name?: string; responses: unknown[]; createdAt: string; updatedAt: string }[]> {
    return this.http.get<{ _id: string; name?: string; responses: unknown[]; createdAt: string; updatedAt: string }[]>(
      `${API_BASE}/api/essay-drafts`
    ).pipe(catchError(() => of([])));
  }

  getEssayDraft(draftId: string): Observable<{
    _id: string;
    userId?: string;
    name?: string;
    responses: unknown[];
    createdAt: string;
    updatedAt: string;
    template?: string;
    customSections?: EssaySectionForDisplay[];
    essaySetup?: { pageCount: number };
    tailoredPromptsCount?: number;
  } | null> {
    return this.http
      .get<{
        _id: string;
        userId?: string;
        name?: string;
        responses: unknown[];
        createdAt: string;
        updatedAt: string;
        template?: string;
        customSections?: EssaySectionForDisplay[];
        essaySetup?: { pageCount: number };
        tailoredPromptsCount?: number;
      }>(`${API_BASE}/api/essay-drafts/${draftId}`)
      .pipe(catchError(() => of(null)));
  }

  saveEssayDraft(
    responses: { sectionTitle: string; sectionOrder: number; promptText: string; promptOrder: number; responseText: string }[],
    draftId?: string,
    name?: string,
    template?: string,
    extras?: { customSections?: EssaySectionForDisplay[]; essaySetup?: { pageCount: number } }
  ): Observable<{ _id: string } | { error: string }> {
    const body: Record<string, unknown> = { responses, draftId, name, template };
    if (extras?.customSections != null) {
      body['customSections'] = extras.customSections;
    }
    if (extras?.essaySetup != null) {
      body['essaySetup'] = extras.essaySetup;
    }
    return this.http.post<{ _id: string }>(`${API_BASE}/api/essay-drafts`, body).pipe(
      catchError((err) => of(err.error && typeof err.error === 'object' ? err.error : { error: 'Failed to save' }))
    );
  }

  deleteEssayDraft(draftId: string): Observable<{ deletedId: string } | null> {
    return this.http
      .delete<{ deletedId: string }>(`${API_BASE}/api/essay-drafts/${draftId}`)
      .pipe(catchError(() => of(null)));
  }

  generateEssayDraft(
    draftId: string,
    body: { citationStyle: 'APA' | 'MLA' } = { citationStyle: 'APA' }
  ): Observable<GenerateEssayResponse | { error: string; code?: number; message?: string; data?: null }> {
    return this.http.post<GenerateEssayResponse>(
      `${API_BASE}/api/essay-drafts/${draftId}/generate`,
      body
    ).pipe(
      catchError((err) => of(err.error && typeof err.error === 'object' ? err.error : { error: 'Failed to generate essay' }))
    );
  }
}
